import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { BRAND } from '@fin/shared/brand';
import { useAuth, type SignInChallenge } from '../lib/auth';

type Stage =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'requestReset' }
  | { kind: 'confirmReset' };

export function Login() {
  const { signIn, respondToChallenge, requestPasswordReset, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secondary, setSecondary] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function handleStepResult(r: SignInChallenge | { user: unknown }) {
    if ('user' in r) {
      navigate(redirectTo, { replace: true });
      return;
    }
    setSecondary('');
    if (r.challenge === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setStage({ kind: 'newPassword' });
    } else {
      setError(`Unexpected sign-in step: ${r.challenge}`);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (stage.kind === 'credentials') {
        handleStepResult(await signIn(email, password));
      } else if (stage.kind === 'newPassword') {
        handleStepResult(await respondToChallenge(secondary));
      } else if (stage.kind === 'requestReset') {
        await requestPasswordReset(email);
        setInfo('We emailed you a 6-digit code. Enter it below with your new password.');
        setStage({ kind: 'confirmReset' });
      } else if (stage.kind === 'confirmReset') {
        await confirmPasswordReset(email, resetCode, resetNewPassword);
        setInfo('Password reset. Sign in with your new password.');
        setResetCode('');
        setResetNewPassword('');
        setPassword('');
        setStage({ kind: 'credentials' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="relative min-h-screen flex items-center justify-center px-4 py-8 overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, ${BRAND.primary} 0%, #000 100%), url(/home-banner.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'multiply',
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{ backgroundImage: 'url(/home-banner.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      />
      <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-white/95 p-6 shadow-2xl backdrop-blur">
        <div
          className="absolute inset-x-0 top-0 h-1.5 rounded-t-2xl"
          style={{ backgroundImage: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.accent})` }}
        />
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/logo.png" alt="FixItNinjaz" className="h-10 w-auto" />
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Worker sign-in</p>
        </div>

        {error ? (
          <div
            role="alert"
            className="mb-4 rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        ) : null}

        {info ? (
          <div
            role="status"
            className="mb-4 rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: BRAND.primary, color: BRAND.primary, backgroundColor: '#F0FDF4' }}
          >
            {info}
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {stage.kind === 'credentials' && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-stone-700">Email</span>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2"
                  style={{ outlineColor: BRAND.primary }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-stone-700">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2"
                  style={{ outlineColor: BRAND.primary }}
                />
              </label>
              <div className="-mt-1 text-right">
                <button
                  type="button"
                  className="text-xs font-medium hover:underline"
                  style={{ color: BRAND.primary }}
                  onClick={() => {
                    setError(null);
                    setInfo(null);
                    setStage({ kind: 'requestReset' });
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}

          {stage.kind === 'requestReset' && (
            <>
              <p className="text-sm text-stone-700">
                Enter your email. We&rsquo;ll send a 6-digit code so you can set a new password.
              </p>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-stone-700">Email</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2"
                  style={{ outlineColor: BRAND.primary }}
                />
              </label>
            </>
          )}

          {stage.kind === 'confirmReset' && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-stone-700">6-digit code (from email)</span>
                <input
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base tracking-widest focus:outline-none focus:ring-2"
                  style={{ outlineColor: BRAND.primary }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-stone-700">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2"
                  style={{ outlineColor: BRAND.primary }}
                />
                <span className="text-xs text-stone-500">At least 12 chars, mixed case + number + symbol.</span>
              </label>
              <button
                type="button"
                className="text-left text-xs font-medium hover:underline"
                style={{ color: BRAND.primary }}
                onClick={async () => {
                  setError(null);
                  setInfo(null);
                  try {
                    await requestPasswordReset(email);
                    setInfo('Code re-sent. Check your inbox.');
                  } catch (err) {
                    setError(err instanceof Error ? err.message : String(err));
                  }
                }}
              >
                Re-send code
              </button>
            </>
          )}

          {stage.kind === 'newPassword' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-stone-700">Set a new password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={secondary}
                onChange={(e) => setSecondary(e.target.value)}
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2"
                style={{ outlineColor: BRAND.primary }}
              />
              <span className="text-xs text-stone-500">At least 12 chars, mixed case + number + symbol.</span>
            </label>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-2 inline-flex items-center justify-center rounded-md px-4 py-3 text-base font-semibold text-white transition disabled:opacity-50"
            style={{ backgroundColor: BRAND.primary }}
          >
            {busy
              ? 'Working\u2026'
              : stage.kind === 'credentials'
                ? 'Sign in'
                : stage.kind === 'requestReset'
                  ? 'Send reset code'
                  : stage.kind === 'confirmReset'
                    ? 'Reset password'
                    : 'Continue'}
          </button>
          {(stage.kind === 'requestReset' || stage.kind === 'confirmReset') && (
            <button
              type="button"
              className="text-xs text-stone-500 hover:underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setResetCode('');
                setResetNewPassword('');
                setStage({ kind: 'credentials' });
              }}
            >
              ← Back to sign in
            </button>
          )}
        </form>
      </div>
    </main>
  );
}
